import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "../store/auth";
import BasicLayout from "../layouts/BasicLayout.vue";

const DEFAULT_AUTHED_HOME = "/project-manage";

export const baseRoutes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Login/index.vue"),
    meta: { public: true, title: "\u767b\u5f55" },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/Register/index.vue"),
    meta: { public: true, title: "Register" },
  },
  {
    path: "/register/:shortKey",
    name: "RegisterWithKey",
    component: () => import("../views/Register/index.vue"),
    meta: { public: true, title: "RegisterWithKey" },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/ForgotPassword/index.vue"),
    meta: { public: true, title: "ForgotPassword" },
  },
  {
    path: "/forgot-password/:shortKey",
    name: "ForgotPasswordWithKey",
    component: () => import("../views/ForgotPassword/index.vue"),
    meta: { public: true, title: "ForgotPasswordWithKey" },
  },
  {
    path: "/privacy",
    name: "Privacy",
    component: () => import("../views/Privacy/index.vue"),
    meta: { public: true, title: "Privacy" },
  },
  {
    path: "/terms",
    name: "Terms",
    component: () => import("../views/Terms/index.vue"),
    meta: { public: true, title: "Terms" },
  },
  {
    path: "/gvi",
    name: "GVI",
    component: () => import("../views/GVI/index.vue"),
    meta: { public: true, title: "GVI" },
  },
  {
    path: "/",
    component: BasicLayout,
    children: [
      {
        path: "",
        name: "Dashboard",
        alias: "/dashboard",
        component: () => import("../views/Dashboard/index.vue"),
        meta: { public: true, title: "Home Page", showHeader: true },
      },
      {
        path: "/payment-success",
        name: "PaymentSuccess",
        component: () => import("../views/PaymentSuccess/index.vue"),
        meta: { title: "Payment Success", showHeader: true },
      },
      {
        path: "/payment-failed",
        name: "PaymentFailed",
        component: () => import("../views/PaymentFailed/index.vue"),
        meta: { title: "Payment Failed", showHeader: true },
      },
      {
        path: "/demo",
        name: "Demo",
        component: () => import("../views/Demo/index.vue"),
        meta: { public: true, title: "Demo", showHeader: true },
      },
      {
        path: "/demo-details",
        name: "DemoDetails",
        component: () => import("../views/PointCloudDetails/demo.vue"),
        meta: { public: true, title: "Point Cloud Details Demo", showHeader: false },
      },
      {
        path: "/project-manage",
        name: "ProjectManage",
        component: () => import("../views/ProjectManage/index.vue"),
        meta: { title: "Project Manage", showHeader: true },
      },
      {
        path: "/user-manage",
        name: "UserManage",
        component: () => import("../views/UserManage/index.vue"),
        meta: { title: "User Manage", showHeader: true },
      },
      {
        path: "/engineering-manage",
        name: "EngineeringManage",
        component: () => import("../views/EngineeringManage/index.vue"),
        meta: { title: "Engineering Manage", showHeader: true },
      },
      {
        path: "/point-cloud-details",
        name: "PointCloudDetails",
        component: () => import("../views/PointCloudDetails/index.vue"),
        meta: { title: "Point Cloud Details" },
      },
      {
        path: "/point-cloud-compare",
        name: "PointCloudCompare",
        component: () => import("../views/PointCloudCompare/index.vue"),
        meta: { title: "Point Cloud Compare" },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/NotFound.vue"),
    meta: { title: "NotFound" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes: baseRoutes,
});

const decodeSidParam = (sid) => {
  try {
    return window.atob(sid);
  } catch (_error) {
    return sid;
  }
};

export function setupRouterGuards(r) {
  let isCheckingAuth = false;

  r.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore();

    const sidQuery = Array.isArray(to.query["sails.sid"])
      ? to.query["sails.sid"][0]
      : to.query["sails.sid"];

    if (typeof sidQuery === "string" && sidQuery.trim()) {
      const decodedSid = decodeSidParam(sidQuery.trim());
      await authStore.bootstrapFromSid(decodedSid);

      const nextQuery = { ...to.query };
      delete nextQuery["sails.sid"];

      return next({
        path: to.path,
        query: nextQuery,
        hash: to.hash,
        replace: true,
      });
    }

    const shareTokenQuery = Array.isArray(to.query.shareToken)
      ? to.query.shareToken[0]
      : to.query.shareToken;
    const isSharePointCloudDetails =
      to.name === "PointCloudDetails" &&
      typeof shareTokenQuery === "string" &&
      shareTokenQuery.trim();
    if (isSharePointCloudDetails) {
      return next();
    }

    const authPages = ["/login"];
    if (authPages.includes(to.path)) {
      if (!authStore.isAuthed && !isCheckingAuth) {
        isCheckingAuth = true;
        try {
          await authStore.fetchProfile({ shouldRedirectOnFail: false });
        } catch {
        } finally {
          isCheckingAuth = false;
        }
      }

      if (authStore.isAuthed) {
        return next(DEFAULT_AUTHED_HOME);
      }
    }

    if (to.name === "Dashboard") {
      await authStore.fetchProfile({ shouldRedirectOnFail: false });
    }

    if (to.meta.public) {
      return next();
    }

    if (!authStore.isAuthed) {
      const ok = await authStore.fetchProfile({ shouldRedirectOnFail: false });
      if (!ok) {
        return next({ path: "/login", query: { redirect: to.fullPath } });
      }
    }

    return next();
  });
}

export default router;
